import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import fetchWithToken from '../../services';
import { resetApp } from '../../state';

const DeleteAccountModal = ({ 
  isOpen, 
  setIsDeleteModalOpen, 
  verificationCode, 
  setVerificationCode, 
  isLoading, 
  setIsLoading, 
  error, 
  setDeleteError,
  deleteStep,
  setDeleteStep,
  feedback,
  setFeedback
}) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      setTimeout(() => {
        modalRef.current?.querySelector('h3')?.focus();
      }, 100);
    } else {
      triggerRef.current?.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleModalClose();
      }

      if (event.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            event.preventDefault();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sendVerificationCode = async () => {
    setIsLoading(true);
    setDeleteError('');

    try {
      const response = await fetchWithToken('/send-delete-verification-code', {
        method: 'POST'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send verification code');
      }

      setDeleteStep('verify-code');
    } catch (error) {
      console.error('Error sending verification code:', error);
      setDeleteError(error.message || 'An error occurred while sending the verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode) {
      setDeleteError('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    setDeleteError('');

    try {
      const response = await fetchWithToken('/verify-delete-code', {
        method: 'POST',
        body: JSON.stringify({ code: verificationCode }),
      });

      const data = await response.json();

      if (response.ok) {
        setDeleteStep('feedback');
        setVerificationCode('');
      } else {
        setDeleteError(data.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setDeleteError('An error occurred while verifying your code');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccount = async () => {
    setIsLoading(true);
    setDeleteError('');

    try {
      const response = await fetchWithToken('/delete-account', {
        method: 'POST',
        body: JSON.stringify({ feedback }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete account');
      }

      resetApp();
      
      // Close modal and navigate to homepage
      setIsDeleteModalOpen(false);
      
      // Navigate to homepage with success message
      navigate('/', { 
        state: { 
          notification: {
            message: 'Your account has been successfully deleted.',
            type: 'success'
          }
        }
      });
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError(error.message || 'An error occurred while deleting your account');
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsDeleteModalOpen(false);
    setDeleteStep('confirm');
    setVerificationCode('');
    setFeedback('');
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const handleModalVerify = () => {
    switch (deleteStep) {
      case 'confirm':
        sendVerificationCode();
        break;
      case 'verify-code':
        verifyCode();
        break;
      case 'feedback':
        deleteAccount();
        break;
      default:
        break;
    }
  };

  const handleModalCancel = () => {
    handleModalClose();
  };

  const renderStepContent = () => {
    switch (deleteStep) {
      case 'confirm':
        return (
          <>
            <h3 id="modal-title-confirm" tabIndex="-1" className="text-lg font-medium mb-2">Delete your account</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data.
            </p>
            <p className="text-gray-600 mb-4">
              If you proceed, we'll send a verification code to your email to confirm this action.
            </p>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleModalCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleModalVerify}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send verification code'}
              </button>
            </div>
          </>
        );
      case 'verify-code':
        return (
          <>
            {/* <h3 className="text-lg font-medium mb-2">Verify your identity</h3> */}
            <p className="text-gray-600 mb-4">We've sent a verification code to your email address. If you haven't received it, please check your junk folder.</p>
            <div className="mb-4">
              <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
              <div className="flex">
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-l"
                  placeholder="Enter 6-digit code"
                  maxLength="6"
                  aria-required="true"
                  aria-invalid={error ? 'true' : 'false'}
                />
                
              </div>
            </div>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={handleModalCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={sendVerificationCode}
                  className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded hover:bg-blue-200"
                  disabled={isLoading}
                >
                  Resend
                </button>
                <button
                  type="button"
                  onClick={handleModalVerify}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                  disabled={isLoading}
                >
                  {isLoading ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </div>
          </>
        );
      case 'feedback':
        return (
          <>
            <h3 id="modal-title-feedback" tabIndex="-1" className="text-lg font-medium mb-2">We're sorry to see you go...</h3>
            <p className="text-gray-600 mb-4">
              Would you mind sharing why you're deleting your account? Your feedback will help us improve Tipl.
            </p>
            <div className="mb-4">
              <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">Feedback</label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Tell us why you're leaving..."
                rows="4"
              />
            </div>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleModalCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleModalVerify}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete account'}
              </button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby={`modal-title-${deleteStep.replace('-','')}`} role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleModalClose}></div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div ref={modalRef} className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
